"use client";

import React from "react";
import type { Column } from "@tanstack/react-table";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  EyeOffIcon,
  FilterIcon,
  FilterXIcon,
} from "lucide-react";
import { cn } from "@repo/shared/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./DropDown";
import { Button } from "./Button";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Filter } from "./DataTableFilters";

interface DataTableColumnHeaderProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>;
  title: string;
  noSort?: boolean;
}

export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  className,
  noSort,
}: DataTableColumnHeaderProps<TData, TValue>) {
  return (
    <div className={cn("flex items-center", className)}>
      <span className="mr-2 whitespace-nowrap">{title}</span>
      {!noSort && column.getCanSort() ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant={
                column.getIsSorted() === "desc" ||
                column.getIsSorted() === "asc"
                  ? "default"
                  : "ghost"
              }
              size="icon"
            >
              {column.getIsSorted() === "desc" ? (
                <ArrowDownIcon size={14} />
              ) : column.getIsSorted() === "asc" ? (
                <ArrowUpIcon size={14} />
              ) : (
                <ArrowUpDownIcon size={14} />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start">
            {column.getIsSorted() && (
              <DropdownMenuItem onClick={() => column.clearSorting()}>
                <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
                Clear Sorting
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => column.toggleSorting(false, true)}>
              <ArrowUpIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
              Asc
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => column.toggleSorting(true, true)}>
              <ArrowDownIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
              Desc
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => column.toggleVisibility(false)}>
              <EyeOffIcon className="mr-2 h-3.5 w-3.5 text-muted-foreground/70" />
              Hide
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
      {column.getCanFilter() &&
      column.columnDef.meta?.filterVariant !== "none" ? (
        <Popover>
          <PopoverTrigger asChild className="self-center">
            <Button
              variant={column.getIsFiltered() ? "default" : "ghost"}
              className="ml-0.5"
              size="icon"
            >
              <FilterIcon size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="top" className="w-full flex flex-col gap-2 p-2">
            {column.getIsFiltered() && (
              <div className="flex flex-row justify-between align-middle gap-2">
                <span className="self-center">Clear Filters</span>

                <Button
                  onClick={() => column.setFilterValue("")}
                  size="icon"
                  className="self-end"
                  variant="ghost"
                >
                  <FilterXIcon size={14} />
                </Button>
              </div>
            )}
            <Filter column={column} />
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
